import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuthStore } from '../stores/authStore';
import { logger } from '../utils/logger';
import { msalGroupMapping } from '../config/groupMapping';

// Group type definition
export interface Group {
  id: string;
  displayName: string;
  description?: string;
  mail?: string;
}

// Define types
interface GroupContextType {
  availableGroups: Group[];
  selectedGroup: string | null;
  selectedGroupObject: Group | null;
  setSelectedGroup: (groupId: string) => void;
  isGroupAccessible: (groupId: string) => boolean;
  isLoading: boolean;
  error: string | null;
}

// Create context with default values
const GroupContext = createContext<GroupContextType>({
  availableGroups: [],
  selectedGroup: null,
  selectedGroupObject: null,
  setSelectedGroup: () => {},
  isGroupAccessible: () => false,
  isLoading: false,
  error: null,
});

// Hardcoded list of groups for admin users
const ADMIN_GROUPS: Group[] = [
  { id: 'Skyway Range', displayName: 'Skyway Range - SAFET' },
  { id: 'DronePort Network', displayName: 'DronePort Network - SAFET' },
  { id: 'TU - SAFE-T', displayName: 'TU - SAFET' },
  { id: 'Asemio', displayName: 'Asemio - ASWP' },
  { id: 'Atlas', displayName: 'Atlas - ASWP' },
  { id: 'inTulsa - ASWP', displayName: 'inTulsa - ASWP' },
  { id: 'inTulsa - CNTRL', displayName: 'inTulsa - CT' },
  { id: 'TU - ASWP', displayName: 'TU - ASWP' },
  { id: 'TU - ARCH', displayName: 'TU - ARCH' },
  { id: 'OSU', displayName: 'OSU - ARCH' },
  { id: 'THEC', displayName: 'THEC - ASWP' },
  { id: 'TCC', displayName: 'TCC - ASWP' },
  { id: 'Black Tech Street', displayName: 'Black Tech Street - GACE' },
  { id: 'OMA - TARMAC', displayName: 'OMA - TARMAC' },
  { id: 'OSU - TARMAC', displayName: 'OSU - TARMAC' },
  { id: 'Tarmac LLC - TARMAC', displayName: 'Tarmac LLC - TARMAC' },
  { id: 'TEDC - TARMAC', displayName: 'TEDC - TARMAC' }
];

// Custom hook for using the group context
export const useGroup = () => useContext(GroupContext);

interface GroupProviderProps {
  children: ReactNode;
}

export const GroupProvider: React.FC<GroupProviderProps> = ({ children }) => {
  const { user, hasRole } = useAuthStore();
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(() => {
    // Initialize from localStorage only if we have a user
    if (user) {
      return localStorage.getItem('selectedGroup');
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState<boolean>(true); // Start with loading true
  const [error, setError] = useState<string | null>(null);
  
  // Get the selected group object based on the selected ID
  const selectedGroupObject = selectedGroup 
    ? availableGroups.find(group => group.id === selectedGroup) || null
    : null;

  // Initialize groups based on user role
  useEffect(() => {
    if (!user) {
      setAvailableGroups([]);
      setSelectedGroup(null);
      localStorage.removeItem('selectedGroup');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      let groups: Group[] = [];
      
      if (hasRole('admin')) {
        // Admins can see all groups
        groups = [...ADMIN_GROUPS];
      } else {
        // Regular users: only show groups they have access to
        const userGroups = user.allGroups || [];
        
        // Check for exact matches using msalGroupMapping
        const mappedUserGroups = userGroups.map(userGroup => msalGroupMapping[userGroup]).filter(Boolean);
        const exactMatches = ADMIN_GROUPS.filter(group => mappedUserGroups.includes(group.id));
        
        // Check for case-insensitive matches (debugging)
        const caseInsensitiveMatches = ADMIN_GROUPS.filter(group => 
          mappedUserGroups.some(mappedGroup => mappedGroup.toLowerCase() === group.id.toLowerCase())
        );
        
        groups = exactMatches;
        
        // Temporary fallback for debugging (remove in production)
        if (groups.length === 0 && userGroups.length > 0) {
          logger.warn('No exact matches found, using case-insensitive fallback for debugging');
          groups = caseInsensitiveMatches;
        }
      }
      
      setAvailableGroups(groups);
      
      if (groups.length > 0) {
        // Check if there's a saved group in localStorage that's still valid and accessible
        const savedGroup = localStorage.getItem('selectedGroup');
        if (savedGroup && groups.some(group => group.id === savedGroup)) {
          setSelectedGroup(savedGroup);
        } else {
          // Only set default if user has access to groups
          const defaultGroup = groups[0].id;
          setSelectedGroup(defaultGroup);
          localStorage.setItem('selectedGroup', defaultGroup);
        }
      } else {
        // No accessible groups - don't set any default
        logger.warn('User has no accessible groups');
        setSelectedGroup(null);
        localStorage.removeItem('selectedGroup');
        setError('No groups available for your account.');
      }
    } catch (err) {
      logger.error('Error setting up groups:', err);
      setError('Failed to load groups. Please try again later.');
      
      // Secure fallback - don't automatically select groups
      setAvailableGroups([]);
      setSelectedGroup(null);
      localStorage.removeItem('selectedGroup');
    } finally {
      setIsLoading(false);
    }
  }, [user, hasRole]);

  // Custom setSelectedGroup function that also updates localStorage
  const handleSetSelectedGroup = (groupId: string) => {
    setSelectedGroup(groupId);
    localStorage.setItem('selectedGroup', groupId);
  };

  // Check if a user can access a specific group
  const isGroupAccessible = (groupId: string): boolean => {
    if (!user) return false;
    
    // Admins can access any group in ADMIN_GROUPS
    if (hasRole('admin')) return ADMIN_GROUPS.some(group => group.id === groupId);
    
    // Regular users can only access groups they belong to
    const userGroups = user.allGroups || [];
    // Map raw Azure AD group names to display names for comparison
    const mappedUserGroups = userGroups.map(userGroup => msalGroupMapping[userGroup]).filter(Boolean);
    return mappedUserGroups.includes(groupId);
  };

  const value = {
    availableGroups,
    selectedGroup,
    selectedGroupObject,
    setSelectedGroup: handleSetSelectedGroup,
    isGroupAccessible,
    isLoading,
    error,
  };

  return (
    <GroupContext.Provider value={value}>
      {children}
    </GroupContext.Provider>
  );
}; 